import React, { useState } from 'react';
import {
  Box, Typography, Paper, Chip, Divider,
} from '@mui/material';
import {
  NewReleases as ReleaseIcon,
  BugReport as BugIcon,
  AutoAwesome as FeatureIcon,
  Build as FixIcon,
  Speed as PerformanceIcon,
  AdminPanelSettings as AdminIcon,
  SupportAgent as OperatorIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import { usePermissions } from '../components/RoleBasedRoute';

// --- 更新情報データ定義 ---

type ChangeType = 'feature' | 'fix' | 'enhancement' | 'performance';

interface ReleaseChange {
  type: ChangeType;
  description: string;
  audience: 'all' | 'admin' | 'operator';
}

interface ReleaseEntry {
  version: string;
  date: string;
  title: string;
  changes: ReleaseChange[];
}

const CHANGE_TYPE_CONFIG: Record<ChangeType, { label: string; color: 'success' | 'error' | 'info' | 'warning'; icon: React.ReactNode }> = {
  feature: { label: '新機能', color: 'success', icon: <FeatureIcon sx={{ fontSize: 14 }} /> },
  fix: { label: 'バグ修正', color: 'error', icon: <BugIcon sx={{ fontSize: 14 }} /> },
  enhancement: { label: '改善', color: 'info', icon: <FixIcon sx={{ fontSize: 14 }} /> },
  performance: { label: 'パフォーマンス', color: 'warning', icon: <PerformanceIcon sx={{ fontSize: 14 }} /> },
};

const RELEASES: ReleaseEntry[] = [
  {
    version: '1.28.1',
    date: '2026/07/15',
    title: 'チャット画面のメッセージ表示不具合を修正',
    changes: [
      { type: 'fix', description: 'メッセージが50件を超えるチャットルームで、最新のメッセージがチャット画面に表示されない不具合を修正（最新5000件を取得）', audience: 'all' },
      { type: 'fix', description: 'お客様側チャット画面でも同様の表示不具合を修正', audience: 'all' },
    ],
  },
  {
    version: '1.28.0',
    date: '2026/05/01',
    title: '外部API連携機能',
    changes: [
      { type: 'feature', description: '外部サービスへCS対応履歴を提供するためのREST APIを実装。チャットルーム一覧・詳細、メッセージ（対応履歴）、対応ログ、統計情報を取得可能', audience: 'admin' },
      { type: 'feature', description: '管理画面に「外部API連携」ページを追加。APIキーの発行・編集・無効化・削除・アクセスログ閲覧が可能', audience: 'admin' },
      { type: 'enhancement', description: 'APIキー認証（キー＋シークレット二重認証）、IPホワイトリスト、レート制限、有効期限、全アクセスの監査ログによるセキュリティ強化', audience: 'admin' },
    ],
  },
  {
    version: '1.27.0',
    date: '2026/04/23',
    title: 'ファイルアップロードのドラッグ&ドロップ対応',
    changes: [
      { type: 'feature', description: 'PC環境でメッセージ入力エリアにファイルをドラッグ&ドロップして添付できるようになりました', audience: 'all' },
      { type: 'enhancement', description: 'ドラッグ中にオーバーレイ表示で視覚的にフィードバック。対応ファイル形式・サイズ制限は従来と同じです', audience: 'all' },
    ],
  },
  {
    version: '1.26.0',
    date: '2026/04/22',
    title: 'セキュリティ強化（ブルートフォース対策・認証改善・脆弱性修正）',
    changes: [
      { type: 'feature', description: 'ブルートフォース対策を導入。ログインは15分間で5回まで、SMS/メールOTPは3回まで、API全体は100回/IPに制限', audience: 'all' },
      { type: 'fix', description: 'テスト用の固定認証情報（ハードコードされたパスワード）をバックエンド・フロントエンドの両方から完全に削除', audience: 'admin' },
      { type: 'fix', description: 'デバッグ用エンドポイント（/test-db, /test-sms）を開発環境のみに制限。本番・テスト環境では無効化', audience: 'admin' },
      { type: 'enhancement', description: 'JWTシークレットを一元管理ユーティリティに統合。環境変数未設定時はエラーで起動を停止し、安全でないデフォルト値の使用を防止', audience: 'admin' },
      { type: 'enhancement', description: 'WebSocketのルーム参加時に認可チェックを追加。エンドユーザーは自分に関連付けられたルームのみ参加可能に', audience: 'all' },
      { type: 'fix', description: 'エンドユーザーのモックログインフォールバックを削除。API障害時に不正なセッションが作成される問題を修正', audience: 'all' },
    ],
  },
  {
    version: '1.25.0',
    date: '2026/04/22',
    title: '監査ログ機能（ログインログ・アクションログ）',
    changes: [
      { type: 'feature', description: 'ログインログ機能を追加。全てのログイン成功・失敗・ログアウトをIPアドレス・アカウント情報付きで記録', audience: 'admin' },
      { type: 'feature', description: 'アクションログ機能を追加。作成・更新・削除・ダウンロード等の全操作をIPアドレス・アカウント情報付きで自動記録', audience: 'admin' },
      { type: 'feature', description: '管理画面に「監査ログ」ページを追加。ログインログ・アクションログをタブで切り替えて閲覧可能', audience: 'admin' },
      { type: 'feature', description: 'ステータス・IPアドレス・日付範囲・アクション種別・リソース種別でフィルタリング可能', audience: 'admin' },
      { type: 'feature', description: 'ログインログ・アクションログのCSVダウンロード機能を追加', audience: 'admin' },
      { type: 'enhancement', description: 'ダウンロード操作時はファイル名も詳細情報として記録。パスワード等の機密情報は自動マスク', audience: 'admin' },
    ],
  },
  {
    version: '1.24.0',
    date: '2026/04/22',
    title: '受信トレイ「全て」の定義変更・ダウンロード期限表示',
    changes: [
      { type: 'enhancement', description: '受信トレイの「全て」フィルタに完了・削除ステータスも含めるよう変更。全ルームを一覧で確認可能に', audience: 'all' },
      { type: 'feature', description: '添付ファイルの直下にダウンロード期限の日付と残り日数を表示。期限切れの場合は赤字で「ダウンロード期限切れ」と表示', audience: 'all' },
      { type: 'enhancement', description: '管理者は「ダウンロード期限: 無制限」、運営者は送信日から90日、お客様は7日の期限を表示', audience: 'all' },
    ],
  },
  {
    version: '1.23.2',
    date: '2026/04/22',
    title: 'ファイルアクセス認可の強化・メール招待E/Uの複数ルーム対応',
    changes: [
      { type: 'fix', description: 'メール招待で参加したエンドユーザーが、複数のルームをまたいでファイルにアクセスできない問題を修正', audience: 'all' },
      { type: 'enhancement', description: 'ファイルアクセス認可をリソース単位の都度検証に改善。よりセキュアなアクセス制御を実現', audience: 'admin' },
      { type: 'fix', description: 'ルームアクセス検証にchat_rooms.participant_emailも認可ソースとして追加。メール招待E/Uの認可精度が向上', audience: 'admin' },
    ],
  },
  {
    version: '1.23.1',
    date: '2026/04/22',
    title: 'ファイル表示・アップロードの修正・セキュリティ強化',
    changes: [
      { type: 'fix', description: 'エンドユーザー画面でファイルアップロード時に403エラーとなる不具合を修正', audience: 'all' },
      { type: 'feature', description: 'エンドユーザー画面で画像をインラインプレビュー表示するよう改善。タップでフルスクリーンビューアーを起動', audience: 'all' },
      { type: 'fix', description: 'iPhoneのSafari/Chromeで画像が表示されない不具合を修正。プレビュー配信方式をプロキシ配信に変更', audience: 'all' },
      { type: 'fix', description: 'ファイル認証における電話番号比較を正規化対応。ハイフンの有無に関わらず正しく認証されるよう改善', audience: 'admin' },
    ],
  },
  {
    version: '1.23.0',
    date: '2026/04/22',
    title: 'ファイルビューアー機能を追加',
    changes: [
      { type: 'feature', description: '添付ファイルのクリックでビューアーが起動。画像・PDF・Office文書（Word/Excel/PowerPoint）をブラウザ内でプレビュー表示', audience: 'all' },
      { type: 'enhancement', description: '画像ビューアーにズーム機能（拡大・縮小・リセット）を搭載', audience: 'all' },
      { type: 'enhancement', description: 'ビューアー非対応のファイル形式はダウンロードにフォールバック', audience: 'all' },
    ],
  },
  {
    version: '1.22.0',
    date: '2026/04/22',
    title: 'チャット画面の右カラムにファイル一覧タブを追加',
    changes: [
      { type: 'feature', description: '右カラムに「顧客情報」と「ファイル一覧」のタブ切り替え機能を追加。アイコンタブで画面を切り替え可能', audience: 'all' },
      { type: 'feature', description: '選択中のルームの添付ファイルをリスト表示。ファイル名・送信者・日時・サイズを確認し、直接ダウンロード可能', audience: 'all' },
    ],
  },
  {
    version: '1.21.1',
    date: '2026/04/22',
    title: 'ファイルアクセスのセキュリティ強化',
    changes: [
      { type: 'fix', description: 'ファイルダウンロード・プレビューにルームレベルの認可チェックを追加。E/Uは自分が所属するルームのファイルのみアクセス可能に', audience: 'all' },
      { type: 'fix', description: 'Base64トークン認証の厳格化。不正なトークンによるアクセスを遮断', audience: 'admin' },
      { type: 'fix', description: '管理者ロール判定を修正。admin/support/engineerロールの無制限ダウンロードが正常に動作するよう改善', audience: 'admin' },
    ],
  },
  {
    version: '1.21.0',
    date: '2026/04/20',
    title: 'S3ファイルストレージ対応・ファイル管理ページ追加',
    changes: [
      { type: 'feature', description: '添付ファイルの保存先をAmazon S3に変更。サーバーローカルストレージから独立し、可用性・スケーラビリティが向上', audience: 'admin' },
      { type: 'feature', description: 'ファイルダウンロードに期限制御を導入。お客様は1週間、オペレーターは3ヶ月、管理者は無制限でダウンロード可能', audience: 'all' },
      { type: 'feature', description: '管理者メニューに「ファイル管理」ページを追加。全ルームの添付ファイルを一覧で検索・ダウンロード可能', audience: 'admin' },
      { type: 'enhancement', description: 'レガシー（ローカル保存済み）ファイルとの後方互換性を維持。既存ファイルは引き続きアクセス可能', audience: 'admin' },
    ],
  },
  {
    version: '1.20.0',
    date: '2026/04/20',
    title: 'ステータス改善: 初期ステータス変更・一時完了追加',
    changes: [
      { type: 'enhancement', description: 'チャットルーム新規作成時の初期ステータスを「未対応」から「返信待ち」に変更。ルーム作成直後はE/Uの返信待ち状態であることを正確に反映', audience: 'all' },
      { type: 'feature', description: '新ステータス「一時完了」を追加。一時的に対応完了としたルームを管理可能に。メッセージ送受信による自動ステータス変更の対象外', audience: 'all' },
    ],
  },
  {
    version: '1.19.2',
    date: '2026/04/17',
    title: 'ルーム一覧にファネル進捗バッジを追加',
    changes: [
      { type: 'feature', description: 'ルーム一覧の「招待配信」列を「進捗」列に変更。送信経路（メール/SMS）アイコンとファネルステップ（OTP・認証・E/U）をバッジで表示', audience: 'all' },
      { type: 'enhancement', description: '各ルームの進捗状況が一目で把握可能に。ツールチップで詳細ステータスを確認可能', audience: 'all' },
    ],
  },
  {
    version: '1.19.1',
    date: '2026/04/17',
    title: 'ファネル分析: SMS到達取得を廃止・仕様整理',
    changes: [
      { type: 'enhancement', description: 'ファネル分析のSMS経路から「SMS到達」ステップを削除。AOSSMS DLR Webhookが未設定のため、取得対象外に変更', audience: 'admin' },
      { type: 'enhancement', description: '全体ファネルの「通知到達」を「メール到達」に変更（SendGrid確認分のみ集計）', audience: 'admin' },
      { type: 'enhancement', description: '招待配信ログ仕様書を更新し、SMS配信結果取得を正式に対象外と明記', audience: 'admin' },
    ],
  },
  {
    version: '1.19.0',
    date: '2026/04/17',
    title: 'ルーム管理ページ追加（一括ステータス変更）',
    changes: [
      { type: 'feature', description: 'サイドバーに「ルーム管理」ページを追加。全ルームの一覧表示・検索・フィルタリングが可能', audience: 'all' },
      { type: 'feature', description: 'チェックボックスで複数ルームを選択し、一括で「完了」「削除」にステータス変更可能に', audience: 'admin' },
      { type: 'feature', description: 'ステータスフィルター・キーワード検索・未活動日数フィルターで対象ルームを絞り込み可能', audience: 'all' },
      { type: 'enhancement', description: '一括操作時にシステムメッセージが自動記録され、操作履歴を追跡可能', audience: 'admin' },
    ],
  },
  {
    version: '1.18.1',
    date: '2026/04/16',
    title: 'ファネル分析のSMS経路データ修正',
    changes: [
      { type: 'fix', description: 'SMS招待OTP認証時にルームIDが記録されない不具合を修正。今後の認証から正しくルームに紐付きます', audience: 'admin' },
      { type: 'fix', description: 'ファネル分析のSMS経路OTP発行・ログイン件数が0件と表示される不具合を修正。電話番号による突合に対応', audience: 'admin' },
    ],
  },
  {
    version: '1.18.0',
    date: '2026/04/16',
    title: 'myTalk利用ファネル分析を追加',
    changes: [
      { type: 'feature', description: '分析・レポートにmyTalk利用ファネル分析を追加。ルーム作成→メール到達→OTP発行→ログイン→E/U送信の各ステップ到達率をグラフ・テーブルで確認可能に', audience: 'admin' },
      { type: 'feature', description: 'メール経路・SMS経路のチャネル別ファネルを並列表示。各ステップの脱落数・脱落率も表示', audience: 'admin' },
      { type: 'enhancement', description: '分析ページに期間フィルター（開始日・終了日）を追加', audience: 'admin' },
    ],
  },
  {
    version: '1.17.0',
    date: '2026/04/10',
    title: 'テンプレート管理ページ追加',
    changes: [
      { type: 'feature', description: 'サイドバーに「テンプレート管理」ページを追加。テンプレートの一覧・作成・編集・削除をまとめて管理可能に', audience: 'all' },
    ],
  },
  {
    version: '1.16.0',
    date: '2026/04/10',
    title: 'ルーム作成時のテンプレート挿入機能',
    changes: [
      { type: 'feature', description: 'ルーム作成モーダルの初期メッセージ欄にテンプレート選択ボタンを追加。定型文をワンクリックで挿入可能に', audience: 'operator' },
    ],
  },
  {
    version: '1.15.3',
    date: '2026/04/10',
    title: 'レスポンスタイム土日対応・ルーム詳細レポート表示改善',
    changes: [
      { type: 'fix', description: 'レスポンスタイムレポートの経過時間計算を毎日10:00〜19:00（土日祝含む）に変更', audience: 'admin' },
      { type: 'fix', description: 'ルーム詳細レポートで削除ステータスの経過時間フォントカラーを黒に変更（完了と同様の表示に統一）', audience: 'admin' },
    ],
  },
  {
    version: '1.15.2',
    date: '2026/03/30',
    title: 'アカウント表示名の入力ルール追加',
    changes: [
      { type: 'enhancement', description: 'アカウント作成・編集時、表示名に姓と名の間のスペースを必須化（苗字抽出の正確性向上）', audience: 'admin' },
      { type: 'enhancement', description: '表示名フィールドに入力ルールの理由を常時表示（個人情報保護のためお客様には苗字のみ表示される旨）', audience: 'admin' },
    ],
  },
  {
    version: '1.15.0',
    date: '2026/03/26',
    title: '削除ステータスのチャットルームへのE/Uアクセス制限',
    changes: [
      { type: 'enhancement', description: '削除ステータスのチャットルームへのエンドユーザーアクセスをブロック（OTP画面・OTP発行・メッセージ閲覧/送信すべて不可）', audience: 'all' },
    ],
  },
  {
    version: '1.15.1',
    date: '2026/03/30',
    title: 'エンドユーザー向け運営者名表示を苗字のみに制限',
    changes: [
      { type: 'fix', description: 'エンドユーザーに表示される運営者名をフルネームから苗字のみに変更（個人情報保護・カスハラ防止）', audience: 'all' },
      { type: 'enhancement', description: '過去のメッセージ履歴も苗字のみで表示されるようマスキング処理を追加', audience: 'all' },
    ],
  },
  {
    version: '1.13.1',
    date: '2026/03/26',
    title: 'レスポンスタイムレポート画面の修正',
    changes: [
      { type: 'fix', description: 'レスポンスタイムレポート画面にサイドメニューが表示されない不具合を修正', audience: 'admin' },
    ],
  },
  {
    version: '1.13.0',
    date: '2026/03/16',
    title: 'オペレーター対応時間の自動記録',
    changes: [
      { type: 'feature', description: 'オペレーターの入室→退室の滞在時間を自動記録', audience: 'admin' },
      { type: 'feature', description: 'ルーム詳細レポートに「対応時間」カラム追加（●分●秒表示）', audience: 'all' },
      { type: 'feature', description: 'CSV出力にも対応時間カラムを追加', audience: 'all' },
      { type: 'enhancement', description: '複数回入退室の場合は合計時間を積み上げ表示', audience: 'all' },
    ],
  },
  {
    version: '1.12.0',
    date: '2026/03/16',
    title: 'レスポンスタイムレポート機能（管理者専用）',
    changes: [
      { type: 'feature', description: '顧客返信→オペレーター返信の経過時間を自動記録（営業時間のみカウント）', audience: 'admin' },
      { type: 'feature', description: 'レスポンスタイムレポート画面（ログ一覧・週次レポート）', audience: 'admin' },
      { type: 'feature', description: 'CSVダウンロード対応（期間指定・週次）', audience: 'admin' },
      { type: 'feature', description: '週次レポート自動生成（毎週月曜1:00 JST）', audience: 'admin' },
      { type: 'enhancement', description: '営業時間: 平日10:00〜19:00、土日祝を除外した経過時間計算', audience: 'admin' },
    ],
  },
  {
    version: '1.11.0',
    date: '2026/03/16',
    title: 'ステータス自動遷移・経過時間表記改善',
    changes: [
      { type: 'feature', description: 'ユーザー返信時にステータスを自動で「未対応」に変更', audience: 'all' },
      { type: 'feature', description: 'オペレーター返信時にステータスを自動で「返答待ち」に変更', audience: 'all' },
      { type: 'feature', description: '60日間操作がないルームを自動で「削除」に変更', audience: 'admin' },
      { type: 'enhancement', description: 'レポート経過時間: 24時間未満→時間表示、24時間以上→日数表示', audience: 'all' },
    ],
  },
  {
    version: '1.10.0',
    date: '2026/03/25',
    title: 'ルーム番号（連番）機能追加',
    changes: [
      { type: 'feature', description: 'チャットルームにユニークなルーム番号（連番）を自動付与', audience: 'all' },
      { type: 'feature', description: 'ルーム一覧にルーム番号（#1, #2...）を表示', audience: 'all' },
      { type: 'feature', description: 'ルーム詳細レポート・CSV出力にルームNoカラムを追加', audience: 'all' },
      { type: 'enhancement', description: 'ルーム番号での検索に対応', audience: 'all' },
      { type: 'enhancement', description: '既存ルームにも作成日時順で番号を自動割り当て', audience: 'all' },
    ],
  },
  {
    version: '1.9.0',
    date: '2026/03/25',
    title: 'マニュアル・FAQ動的管理機能',
    changes: [
      { type: 'feature', description: 'マニュアル・FAQの追加・編集・削除機能（管理者のみ）', audience: 'admin' },
      { type: 'feature', description: '一括操作機能（一括削除・一括公開・一括非公開）', audience: 'admin' },
      { type: 'feature', description: '記事の並び替え機能（上下矢印で順序変更）', audience: 'admin' },
      { type: 'feature', description: '記事の公開/非公開切り替え機能', audience: 'admin' },
      { type: 'enhancement', description: 'マニュアル・FAQがデータベース管理に移行（動的に更新可能に）', audience: 'all' },
    ],
  },
  {
    version: '1.8.0',
    date: '2026/03/25',
    title: 'ヘルプ・更新情報ページ追加',
    changes: [
      { type: 'feature', description: 'マニュアル・FAQページを追加（運営者・管理者の権限に応じた表示）', audience: 'all' },
      { type: 'feature', description: '更新情報（リリースノート）ページを追加', audience: 'all' },
      { type: 'enhancement', description: 'ナビゲーションにヘルプ・更新情報へのリンクを追加', audience: 'all' },
    ],
  },
  {
    version: '1.7.0',
    date: '2026/03/16',
    title: 'チャット画面機能改善・ステータスラベル統一',
    changes: [
      { type: 'feature', description: 'ルーム一覧に経過時間でのソート機能を追加', audience: 'all' },
      { type: 'feature', description: '案件No.での検索機能を追加', audience: 'all' },
      { type: 'feature', description: 'ルーム詳細レポートに経過時間（時間単位）カラムを追加', audience: 'all' },
      { type: 'fix', description: 'ステータスラベルの統一（「返信待ち」「保留」の表記を全画面で統一）', audience: 'all' },
      { type: 'enhancement', description: '検索窓にヘルプアイコン（?）を追加し、検索対象項目をツールチップで表示', audience: 'all' },
    ],
  },
  {
    version: '1.6.0',
    date: '2026/02/10',
    title: 'レポート機能強化',
    changes: [
      { type: 'feature', description: 'ルーム詳細レポートのCSV出力機能（UTF-8 / Shift-JIS 対応）', audience: 'all' },
      { type: 'feature', description: 'アカウント別サマリーレポートの追加', audience: 'all' },
      { type: 'enhancement', description: 'レポート期間フィルタの改善', audience: 'all' },
    ],
  },
  {
    version: '1.5.0',
    date: '2026/01/20',
    title: 'SMS気づき通知機能',
    changes: [
      { type: 'feature', description: '運営者のメッセージ送信時にお客様へSMS通知を自動送信', audience: 'all' },
      { type: 'enhancement', description: '同一ルームへの通知は5分間隔に制限（スパム防止）', audience: 'all' },
      { type: 'feature', description: 'SMS配信ステータスの表示（配信済み/失敗アイコン）', audience: 'all' },
    ],
  },
  {
    version: '1.4.0',
    date: '2025/12/15',
    title: 'チャット機能改善',
    changes: [
      { type: 'feature', description: 'ファイル添付機能（画像・PDF・ドキュメント対応）', audience: 'all' },
      { type: 'feature', description: 'タイピング通知の表示', audience: 'all' },
      { type: 'enhancement', description: 'チャットUIのデザイン改善', audience: 'all' },
      { type: 'fix', description: 'WebSocket切断時の自動再接続を改善', audience: 'all' },
    ],
  },
  {
    version: '1.3.0',
    date: '2025/11/20',
    title: '固定タグ・メモ機能',
    changes: [
      { type: 'feature', description: 'チャットルームへの固定タグ設定機能', audience: 'all' },
      { type: 'feature', description: 'チャットルームのメモ機能', audience: 'all' },
      { type: 'feature', description: '固定タグカテゴリ・選択肢の管理機能', audience: 'admin' },
    ],
  },
  {
    version: '1.2.0',
    date: '2025/10/15',
    title: 'セキュリティ強化',
    changes: [
      { type: 'feature', description: 'IP制限機能の追加', audience: 'admin' },
      { type: 'enhancement', description: 'ログイン認証の強化', audience: 'all' },
      { type: 'feature', description: 'メール認証（OTP）によるお客様認証', audience: 'all' },
    ],
  },
  {
    version: '1.1.0',
    date: '2025/09/01',
    title: 'アカウント管理・権限制御',
    changes: [
      { type: 'feature', description: '管理者・運営者の権限分離', audience: 'admin' },
      { type: 'feature', description: 'アカウント作成・編集・削除機能', audience: 'admin' },
      { type: 'enhancement', description: 'ルーム一覧のフィルタリング改善', audience: 'all' },
    ],
  },
  {
    version: '1.0.0',
    date: '2025/07/01',
    title: '初回リリース',
    changes: [
      { type: 'feature', description: 'チャットルーム作成・管理機能', audience: 'all' },
      { type: 'feature', description: 'SMS招待によるお客様参加フロー', audience: 'all' },
      { type: 'feature', description: 'リアルタイムチャット（WebSocket）', audience: 'all' },
      { type: 'feature', description: '管理画面（ダッシュボード）', audience: 'all' },
    ],
  },
];

// --- コンポーネント ---

const ReleaseNotes: React.FC = () => {
  const { isAdmin } = usePermissions();
  const [filterType, setFilterType] = useState<ChangeType | 'all'>('all');

  const userRole = isAdmin() ? 'admin' : 'operator';

  const filteredReleases = RELEASES.map(release => ({
    ...release,
    changes: release.changes.filter(c =>
      (c.audience === 'all' || c.audience === userRole) &&
      (filterType === 'all' || c.type === filterType)
    ),
  })).filter(r => r.changes.length > 0);

  return (
    <AdminLayout>
      <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <ReleaseIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            更新情報
          </Typography>
          <Chip
            icon={userRole === 'admin' ? <AdminIcon sx={{ fontSize: 16 }} /> : <OperatorIcon sx={{ fontSize: 16 }} />}
            label={userRole === 'admin' ? '管理者' : '運営者'}
            size="small"
            color={userRole === 'admin' ? 'error' : 'primary'}
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Chip
            label="すべて"
            onClick={() => setFilterType('all')}
            variant={filterType === 'all' ? 'filled' : 'outlined'}
            color="default"
          />
          {(Object.entries(CHANGE_TYPE_CONFIG) as [ChangeType, typeof CHANGE_TYPE_CONFIG[ChangeType]][]).map(([type, config]) => (
            <Chip
              key={type}
              icon={config.icon as React.ReactElement}
              label={config.label}
              onClick={() => setFilterType(type)}
              variant={filterType === type ? 'filled' : 'outlined'}
              color={config.color}
            />
          ))}
        </Box>

        {filteredReleases.map((release, idx) => (
          <Paper key={release.version} sx={{ mb: 3, overflow: 'hidden' }}>
            <Box sx={{
              p: 2, bgcolor: idx === 0 ? 'primary.main' : 'grey.100',
              color: idx === 0 ? 'white' : 'text.primary',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`v${release.version}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: idx === 0 ? 'rgba(255,255,255,0.2)' : 'white',
                    color: idx === 0 ? 'white' : 'primary.main',
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                  {release.title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {release.date}
                </Typography>
              </Box>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              {release.changes.map((change, cIdx) => (
                <Box key={cIdx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75 }}>
                  <Chip
                    icon={CHANGE_TYPE_CONFIG[change.type].icon as React.ReactElement}
                    label={CHANGE_TYPE_CONFIG[change.type].label}
                    size="small"
                    color={CHANGE_TYPE_CONFIG[change.type].color}
                    variant="outlined"
                    sx={{ minWidth: 110, justifyContent: 'flex-start', height: 24, mt: 0.25 }}
                  />
                  <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                    {change.description}
                  </Typography>
                  {change.audience === 'admin' && (
                    <Chip label="管理者" size="small" color="error" variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem', ml: 'auto', flexShrink: 0 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    </AdminLayout>
  );
};

export default ReleaseNotes;
